import { PropsWithChildren, ReactNode } from 'react';
import { UnsavedChangesDialog } from '@mews-commander/ui';
import { FloatingPanel } from '@mews/b2b-ui';
import {
    LocalizedText,
    lt,
    showErrorAlert,
    showSuccessAlert,
    SubmitHandler,
    useDialog,
    useFormControls,
    UseFormControlsParams,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button } from '@optimus-web/core';
import { EmailValidationProvider } from '@mews-commander/form';

export type SsoEditPanelProps<T extends Record<string, unknown>> = UseFormControlsParams<T> &
    PropsWithChildren<{
        defaultValues: T;
        title: ReactNode;
        active: boolean;
        onClose: () => void;
    }>;

export function SsoEditPanel<T extends Record<string, unknown>>({
    title,
    active,
    onClose,
    children,
    onSubmit,
    ...formProps
}: SsoEditPanelProps<T>) {
    const [{ active: showUnsavedChangesDialog }, setUnsavedChangesDialogVisibility] = useDialog();
    const handleSubmit: SubmitHandler<T> = async (data, extra) => {
        try {
            await onSubmit(data, extra);
            onClose();
            showSuccessAlert({
                title: lt('ChangesSaved'),
            });
        } catch (error) {
            showErrorAlert({ description: <>{error.message}</> });
        }
    };

    const { defaultValues } = formProps;
    const { renderFormContent, isSubmitting, hasDirtyFields, reset } = useFormControls({
        ...formProps,
        defaultValues,
        mode: 'onBlur',
        onSubmit: handleSubmit,
    });
    const handleClose = hasDirtyFields ? setUnsavedChangesDialogVisibility : onClose;

    return (
        <>
            <FloatingPanel
                active={active}
                onClose={handleClose}
                modal
                title={title}
                loading={isSubmitting}
                footer={{
                    primary: (
                        <Button
                            size="medium"
                            type="submit"
                            variant="primary"
                            form={formProps.id}
                            loading={isSubmitting}
                        >
                            <LocalizedText textKey={TextKey.Save} />
                        </Button>
                    ),
                    secondary: (
                        <Button size="medium" variant="tertiary" type="button" onClick={handleClose}>
                            <LocalizedText textKey={TextKey.GoBack} />
                        </Button>
                    ),
                }}
            >
                <EmailValidationProvider>{renderFormContent(children)}</EmailValidationProvider>
            </FloatingPanel>

            <UnsavedChangesDialog
                active={showUnsavedChangesDialog}
                onCancel={setUnsavedChangesDialogVisibility}
                onDiscardChanges={() => {
                    setUnsavedChangesDialogVisibility();
                    reset(defaultValues);
                    onClose();
                }}
            />
        </>
    );
}
